import React, { useState, useEffect, useRef } from 'react';
import { Book, FileText, Save, Sparkles, Loader2, Eye } from 'lucide-react';
import { api } from '../../lib/api';
import ProjectEnhanceModal from './ProjectEnhanceModal';
import ProjectDetailModal from './ProjectDetailModal';

// --- Main Component ---
type Props = {
  lessonId: number;
  courseId: number;
  form: any;
  setForm: (val: any) => void;
  onSave: () => void;
  // 🔥 Modified: Removed onEnterWorkshop, simplified to just plan creation
  onCreateNewPlan: () => void; 
};

// Parse learning objectives JSON string
type LearningObjectives = {
  "核心能力模型": string;
  "知识点拆解": string;
  "AI实战项目": string;
  "解决痛点": string;
  "学完马上能做什么": string;
  "未来能做的大事": string;
  "知识进阶路线": string;
};

export const LessonMetaEditor: React.FC<Props> = ({ 
  lessonId, courseId, form, setForm, onSave, onCreateNewPlan 
}) => {
  const [isEnhanceModalOpen, setIsEnhanceModalOpen] = useState(false);
  const [enhancedContent, setEnhancedContent] = useState<string>('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const shouldSaveRef = useRef(false);

  // Effect to trigger save after form update
  useEffect(() => {
    if (shouldSaveRef.current) {
      shouldSaveRef.current = false;
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        onSave();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [form.learning_objectives, onSave]);

  // Parse learning objectives
  const learningObjectives: LearningObjectives = form.learning_objectives 
    ? typeof form.learning_objectives === 'string' 
      ? JSON.parse(form.learning_objectives) 
      : form.learning_objectives
    : {
        "核心能力模型": "",
        "知识点拆解": "",
        "AI实战项目": "",
        "解决痛点": "",
        "学完马上能做什么": "",
        "未来能做的大事": "",
        "知识进阶路线": ""
      };

  // Handle learning objectives change
  const handleLearningObjectiveChange = (key: keyof LearningObjectives, value: string) => {
    const updatedObjectives = { ...learningObjectives, [key]: value };
    setForm({ ...form, learning_objectives: JSON.stringify(updatedObjectives) });
  };

  // Handle project enhance
  const handleProjectEnhance = async () => {
    setIsEnhancing(true);
    try {
      // 根据 courseId 决定 prompt_id
      let promptId: number | undefined;
      if (courseId === 5) {
        promptId = 9;
      } else if (courseId === 6) {
        promptId = 16;
      }

      const payload: any = { lesson_id: lessonId };
      if (promptId !== undefined) {
        payload.prompt_id = promptId;
      }

      const response = await api.post('/slides/lesson/project-enhance/', payload);
      
      if (response.data && response.data.content) {
        setEnhancedContent(response.data.content);
        setIsEnhanceModalOpen(true);
      }
    } catch (error) {
      console.error('Project enhance failed:', error);
      alert('AI优化失败，请稍后重试');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle confirm enhanced content
  const handleConfirmEnhancedContent = (content: string) => {
    // Directly update the form with new content
    const updatedObjectives = { ...learningObjectives, "AI实战项目": content };
    const newForm = { ...form, learning_objectives: JSON.stringify(updatedObjectives) };
    
    // Set flag to trigger save after state update
    shouldSaveRef.current = true;
    
    // Update form state
    setForm(newForm);
    setIsEnhanceModalOpen(false);
  };

  // Handle regenerate
  const handleRegenerate = async () => {
    setIsEnhancing(true);
    try {
      // 根据 courseId 决定 prompt_id
      let promptId: number | undefined;
      if (courseId === 5) {
        promptId = 9;
      } else if (courseId === 6) {
        promptId = 16;
      }

      const payload: any = { lesson_id: lessonId };
      if (promptId !== undefined) {
        payload.prompt_id = promptId;
      }

      const response = await api.post('/slides/lesson/project-enhance/', payload);
      
      if (response.data && response.data.content) {
        setEnhancedContent(response.data.content);
      }
    } catch (error) {
      console.error('Project enhance failed:', error);
      alert('AI优化失败，请稍后重试');
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 text-gray-400 text-sm"><Book size={16} /> <span>Lesson ID: {lessonId}</span></div>
      </div>

      <div className="space-y-6">
        {/* Lesson Title */}
        <input 
          type="text" 
          value={form.title} 
          onChange={e => setForm({...form, title: e.target.value})} 
          className="w-full text-xl font-bold border-b-2 border-gray-200 focus:border-brand-500 outline-none py-1" 
          autoFocus={form.title === "New Lesson"} 
        />

        {/* Learning Objectives Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Learning Objectives</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">核心能力模型</label>
              <input 
                type="text" 
                value={learningObjectives["核心能力模型"]} 
                onChange={e => handleLearningObjectiveChange("核心能力模型", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm" 
              />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">知识点拆解</label>
              <textarea 
                value={learningObjectives["知识点拆解"]} 
                onChange={e => handleLearningObjectiveChange("知识点拆解", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm min-h-[100px]" 
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase">AI实战项目</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsDetailModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full hover:bg-blue-600 transition-all shadow-sm hover:shadow-md"
                    title="查看详情"
                  >
                    <Eye size={12} />
                    <span>详情</span>
                  </button>
                  <button
                    onClick={handleProjectEnhance}
                    disabled={isEnhancing}
                    className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-600 hover:to-pink-600 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    title="AI创意优化"
                  >
                    {isEnhancing ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        <span>生成中...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={12} />
                        <span>创意</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              <textarea 
                value={learningObjectives["AI实战项目"]} 
                onChange={e => handleLearningObjectiveChange("AI实战项目", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm min-h-[80px]" 
              />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">解决痛点</label>
              <textarea 
                value={learningObjectives["解决痛点"]} 
                onChange={e => handleLearningObjectiveChange("解决痛点", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm min-h-[100px]" 
              />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">学完马上能做什么</label>
              <textarea 
                value={learningObjectives["学完马上能做什么"]} 
                onChange={e => handleLearningObjectiveChange("学完马上能做什么", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm min-h-[80px]" 
              />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">未来能做的大事</label>
              <textarea 
                value={learningObjectives["未来能做的大事"]} 
                onChange={e => handleLearningObjectiveChange("未来能做的大事", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm min-h-[80px]" 
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">知识进阶路线</label>
              <textarea 
                value={learningObjectives["知识进阶路线"]} 
                onChange={e => handleLearningObjectiveChange("知识进阶路线", e.target.value)} 
                className="w-full border rounded-lg p-3 text-sm min-h-[80px]" 
              />
            </div>
          </div>
        </div>
        
        {/* Action Bar */}
        <div className="flex justify-between pt-4 border-t mt-6 items-center">
            <div className="flex gap-3">
                {/* 🔥 Create Plan Only */}
                <button 
                    onClick={onCreateNewPlan} 
                    className="flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 text-sm transition-colors font-medium border border-blue-200"
                >
                    <FileText size={16} /> New Plan
                </button>
                {/* Removed New Slides Button */}
            </div>
            <button onClick={onSave} className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2 rounded-lg hover:bg-gray-800 text-sm font-medium shadow-sm"><Save size={16} /> Save Meta</button>
        </div>
      </div>

      {/* Project Enhance Modal */}
      <ProjectEnhanceModal
        isOpen={isEnhanceModalOpen}
        onClose={() => setIsEnhanceModalOpen(false)}
        content={enhancedContent}
        onConfirm={handleConfirmEnhancedContent}
        onRegenerate={handleRegenerate}
        isRegenerating={isEnhancing}
      />

      {/* Project Detail Modal */}
      <ProjectDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        content={learningObjectives["AI实战项目"]}
      />
    </div>
  );
};
